import { useContext } from 'react';
import * as analytics from '../analytics';
import { AnalyticsContext } from '../context/AnalyticsProvider';
import { Theme } from '@cmctechnology/webinvest-constants';
import { ActivityStatus, EventType, PageView, UserAttributes } from '../analytics';

export type AnyEventAttributes = {
  [key: string]: string;
};

export interface IUseAnalyticsContextConfig {
  clientId?: string;
  accountId?: string;
  theme?: Theme;
}

/**
 * At the top level call useAnalyticsContext with the config to set values, or call returned methods if they change
 * @param config
 */
export const useAnalyticsContext = (config?: IUseAnalyticsContextConfig) => {
  const { loggedIn, setLoggedIn, clientId, setClientId, accountId, setAccountId, theme, setTheme } = useContext(AnalyticsContext); // eslint-disable-line @typescript-eslint/no-unused-vars

  if (config?.accountId) {
    setAccountId(config.accountId);
  }

  if (config?.theme) {
    setTheme(config.theme);
  }

  if (config?.clientId) {
    setClientId(config.clientId);
  }

  const logout = analytics.logout;

  const identifyUser = async (attributes?: UserAttributes) => {
    try {
      if (clientId) {
        const userAttributes: UserAttributes = {
          ...attributes,
          Theme: theme,
          ActivityStatus: ActivityStatus.Active,
          displayName: `Id ${clientId}` // Required by Fullstory
        };
        await analytics.identifyUser(clientId.toString(), userAttributes);
        setLoggedIn(true);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const pageView = async (page: PageView, requireLoggedIn?: boolean) => {
    // call analytics.pageView with accountId, theme, etc parameters from context.
    try {
      await analytics.pageView(page, { AccountId: accountId }, requireLoggedIn);
    } catch (error) {
      console.error(error);
    }
  };

  const trackEvent = async (eventName: string, type: EventType, attributes: AnyEventAttributes) => {
    // call analytics.trackEvent and populate EventAttributes from context
    // could even provide specialised versions of trackEvents for different event types that will pass extra parameters
    try {
      await analytics.trackEvent(eventName, { ...attributes, AccountId: accountId }, { type });
    } catch (error) {
      console.error(error);
    }
  };

  return { loggedIn, clientId, accountId, theme, setClientId, setAccountId, setTheme, identifyUser, pageView, trackEvent, logout };
};
